import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { GALLERY_IMAGES } from "../data/site";
import { RevealWordsOnScroll } from "./Reveal";

// A cinematic, drag-to-explore horizontal image slider.
export default function ShowcaseSlider() {
  const trackRef = useRef(null);
  const [constraint, setConstraint] = useState(0);
  const slides = GALLERY_IMAGES.slice(0, 10);

  const measure = () => {
    const el = trackRef.current;
    if (!el) return;
    setConstraint(el.scrollWidth - el.offsetWidth);
  };

  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.offsetWidth * 0.7), behavior: "smooth" });
  };

  return (
    <section data-testid="showcase-slider" className="bg-navy text-ivory overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-24 md:pt-32">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <span className="overline text-gold">A Cinematic Walkthrough</span>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tight">
              <RevealWordsOnScroll text="Drag to explore the hall." />
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="showcase-prev"
              onClick={() => nudge(-1)}
              className="grid h-12 w-12 place-items-center rounded-full border border-ivory/25 text-ivory/80 transition-all duration-300 hover:border-gold hover:text-gold hover:scale-105"
              aria-label="Previous"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="showcase-next"
              onClick={() => nudge(1)}
              className="grid h-12 w-12 place-items-center rounded-full border border-ivory/25 text-ivory/80 transition-all duration-300 hover:border-gold hover:text-gold hover:scale-105"
              aria-label="Next"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-14 md:mt-16 pb-24 md:pb-32">
        <motion.div
          ref={trackRef}
          onMouseEnter={measure}
          onTouchStart={measure}
          className="no-scrollbar flex cursor-grab gap-5 overflow-x-auto px-5 md:px-10 pb-6 active:cursor-grabbing"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {slides.map((s, i) => (
            <motion.figure
              key={s.src}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group relative shrink-0"
              style={{ scrollSnapAlign: "start" }}
            >
              <div
                className={`relative overflow-hidden rounded-sm border border-ivory/12 ${
                  i % 3 === 0 ? "h-[420px] w-[320px] md:h-[560px] md:w-[440px]" : "h-[420px] w-[280px] md:h-[560px] md:w-[380px]"
                }`}
              >
                <img
                  src={s.src}
                  alt={s.alt}
                  draggable="false"
                  className="h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-95" />
                <figcaption className="absolute inset-x-0 bottom-0 translate-y-2 p-6 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <span className="overline text-gold">{s.cat}</span>
                  <p className="mt-1 font-serif text-xl font-light text-ivory">{s.alt}</p>
                </figcaption>
              </div>
            </motion.figure>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
